import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiJson } from '../lib/api'
import type { FullPreviewKind } from './previewKind'
import type { AttachmentDetail, PreviewItem } from './types'

type Tab = 'preview' | 'pages' | 'text'

/**
 * Belge goruntuleyici: tam onizleme + sayfa goruntuleri + cikarilan metin.
 *
 * ## Uc sekme
 *
 *   Onizleme  -> dosyanin KENDI gorunumu (`fullPreviewUrl` varsa): PDF,
 *                PDF'e cevrilmis Office belgesi veya sandbox'ta HTML.
 *                Varsa VARSAYILAN: sayfa duzeni ve bicimlendirme yalnizca
 *                burada dogru.
 *   Sayfalar  -> taranmis / font kodlamasi bozuk PDF sayfalarinin PNG'leri.
 *                Onceki halde PDF'lerde GIZLENIYORDU (iframe onun yerini
 *                aliyordu) - ama iframe cerceve basliklari yuzunden bos
 *                kaliyordu ve taranmis sayfalar hic gorulemiyordu.
 *   Metin     -> cikarilan metin (modelin gordugu sey).
 *
 * ## Metin `<pre>` icinde, markdown hattina GIRMIYOR
 *
 * Cikarilan metin GUVENILMEYEN icerik: kullanicinin yukledigi bir dosyadan
 * geliyor. Markdown olarak islemek, belgenin link ve bicimlendirme
 * uretmesine izin vermek olurdu. React'in kendi kacislari yeterli.
 *
 * ## Kopyalama butonu neden `onTextChange` ile YUKARI tasiniyor
 *
 * Indir/Kapat modalin BASLIGINDA (`AttachmentModal`); kopyalanacak METIN ise
 * burada, asenkron cekilen `detail` state'inde yasiyor. Callback bu boslugu
 * dolduruyor: metin degistikce modal'a haber veriliyor.
 */
export function DocumentView({
  item,
  onTextChange,
}: {
  item: PreviewItem
  /** Su an kopyalanabilir metin - yoksa/hazir degilse `null`. */
  onTextChange?(text: string | null): void
}) {
  const { t } = useTranslation()
  const fullKind = item.fullPreviewUrl ? (item.fullPreviewKind ?? null) : null
  const [detail, setDetail] = useState<AttachmentDetail | null>(null)
  const [loading, setLoading] = useState(Boolean(item.detailUrl))
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>(fullKind ? 'preview' : 'text')
  const [page, setPage] = useState(0)

  // `onTextChange`i ref uzerinden: efektin bagimliligi olmasi (inline arrow
  // fonksiyon her render'da yeni referans) gereksiz yeniden calismalara
  // yol acardi.
  const onTextChangeRef = useRef(onTextChange)
  onTextChangeRef.current = onTextChange

  // `t`yi ref uzerinden: efektin bagimliligi olmasi, dil degistiginde
  // onizlemeyi YENIDEN CEKMEK demek olurdu.
  const tRef = useRef(t)
  tRef.current = t

  // Modal icinde baska bir eke gecildiginde sekme o ekin varsayilanina donuyor.
  useEffect(() => {
    setTab(fullKind ? 'preview' : 'text')
    setPage(0)
  }, [item.detailUrl, item.fullPreviewUrl, fullKind])

  useEffect(() => {
    if (!item.detailUrl) {
      setDetail(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    apiJson<AttachmentDetail>(item.detailUrl)
      .then((data) => {
        if (cancelled) return
        setDetail(data)
        // Tam onizleme yoksa: sayfa goruntusu varsa onu goster - taranmis bir
        // PDF'te metin bos veya bozuk olabiliyor.
        if (!fullKind) setTab(data.pages.length > 0 ? 'pages' : 'text')
      })
      .catch(() => {
        if (!cancelled) setError(tRef.current('attach.doc.previewFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [item.detailUrl, fullKind])

  // Kopyalanabilir metni MODAL BASLIGINA bildirir - bkz. dosya basi notu.
  useEffect(() => {
    const text = item.detailUrl ? detail?.text : item.localText
    onTextChangeRef.current?.(text && text.trim() ? text : null)
    return () => onTextChangeRef.current?.(null)
  }, [item.detailUrl, item.localText, detail])

  const hasTextSource = Boolean(item.detailUrl || item.localText)

  // Ne tam onizleme ne sunucu detayi var: bekleyen (henuz yuklenmemis) chip.
  if (!fullKind && !item.detailUrl) {
    return item.localText ? (
      <TextPane text={item.localText} truncated={false} />
    ) : (
      <p className="preview-note">{t('attach.doc.notUploaded')}</p>
    )
  }

  const pages = detail?.pages ?? []
  const showTabs = Boolean(fullKind) || pages.length > 0

  return (
    <div className="doc-view">
      {showTabs && (
        <div className="doc-tabs" role="tablist">
          {fullKind && (
            <TabButton active={tab === 'preview'} onClick={() => setTab('preview')}>
              {t('attach.doc.preview')}
            </TabButton>
          )}
          {pages.length > 0 && (
            <TabButton active={tab === 'pages'} onClick={() => setTab('pages')}>
              {t('attach.doc.pages', { count: pages.length })}
            </TabButton>
          )}
          {hasTextSource && (
            <TabButton active={tab === 'text'} onClick={() => setTab('text')}>
              {t('attach.doc.text')}
            </TabButton>
          )}
        </div>
      )}

      {tab === 'preview' && fullKind && item.fullPreviewUrl ? (
        <FullPreview
          kind={fullKind}
          url={item.fullPreviewUrl}
          filename={item.filename}
          warnHtml={Boolean(item.warnHtml)}
        />
      ) : tab === 'pages' && pages.length > 0 ? (
        <div className="doc-pages">
          <img
            src={pages[page]?.url}
            alt={t('attach.doc.pageAlt', { page: page + 1 })}
            width={pages[page]?.width ?? undefined}
            height={pages[page]?.height ?? undefined}
            // Yalnizca gorunen sayfa yukleniyor: 30 sayfalik bir PDF'in
            // tamamini cekmek megabaytlar demek.
            loading="lazy"
          />
          <div className="doc-page-nav">
            <button
              type="button"
              className="copy"
              onClick={() => setPage((current) => Math.max(0, current - 1))}
              disabled={page === 0}
            >
              ‹
            </button>
            <span aria-live="polite">
              {t('attach.doc.pageNav', { page: page + 1, total: pages.length })}
            </span>
            <button
              type="button"
              className="copy"
              onClick={() => setPage((current) => Math.min(pages.length - 1, current + 1))}
              disabled={page === pages.length - 1}
            >
              ›
            </button>
          </div>
        </div>
      ) : (
        <TextTab
          detailUrl={item.detailUrl}
          localText={item.localText}
          detail={detail}
          loading={loading}
          error={error}
        />
      )}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick(): void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={active ? 'copy doc-tab-active' : 'copy'}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function TextTab({
  detailUrl,
  localText,
  detail,
  loading,
  error,
}: {
  detailUrl?: string
  localText?: string
  detail: AttachmentDetail | null
  loading: boolean
  error: string | null
}) {
  const { t } = useTranslation()
  if (!detailUrl) {
    return localText ? (
      <TextPane text={localText} truncated={false} />
    ) : (
      <p className="preview-note">{t('attach.doc.notUploaded')}</p>
    )
  }
  if (loading) return <p className="preview-note">{t('common.loading')}</p>
  if (error || !detail) {
    return (
      <p className="preview-note" role="alert">
        {error ?? t('attach.doc.noPreview')}
      </p>
    )
  }
  if (!detail.text.trim()) {
    return <p className="preview-note">{detail.extract_note ?? t('attach.doc.noText')}</p>
  }
  return <TextPane text={detail.text} truncated={detail.text_truncated} />
}

/**
 * Dosyanin kendi gorunumu, `iframe` icinde.
 *
 * Office belgelerinde once ayni URL `fetch` ile isteniyor: donusum sunucuda
 * saniyeler surebiliyor ve LibreOffice eksikse/donusum basarisizsa iframe
 * icinde ham bir JSON hatasi gostermek yerine ANLASILIR bir mesaj veriyoruz.
 * Istek basarili olunca PDF sunucuda onbellekte oldugu icin iframe'in ayni
 * istegi aninda donuyor.
 *
 * HTML iki kat sandbox'ta: bu `sandbox` attribute'u ve sunucunun CSP `sandbox`
 * direktifi (`allow-same-origin` ikisinde de YOK - sayfa uygulamaya ve oturuma
 * erisemez). Baska bir kullanicidan gelen HTML'de once uyari gosteriliyor.
 */
function FullPreview({
  kind,
  url,
  filename,
  warnHtml,
}: {
  kind: FullPreviewKind
  url: string
  filename: string
  warnHtml: boolean
}) {
  const { t } = useTranslation()
  const [state, setState] = useState<'checking' | 'ready' | 'error'>(
    kind === 'office' ? 'checking' : 'ready',
  )
  const [message, setMessage] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(!warnHtml)

  const tRef = useRef(t)
  tRef.current = t

  useEffect(() => {
    setConfirmed(!warnHtml)
  }, [url, warnHtml])

  useEffect(() => {
    if (kind !== 'office') {
      setState('ready')
      return
    }
    let cancelled = false
    setState('checking')
    setMessage(null)
    fetch(url)
      .then(async (response) => {
        if (cancelled) return
        if (response.ok) {
          // Govdeyi okumaya gerek yok - iframe onbellekten alacak.
          void response.body?.cancel()
          setState('ready')
          return
        }
        const code = await response
          .json()
          .then((body) => body?.error?.code ?? body?.detail?.code)
          .catch(() => null)
        if (cancelled) return
        setMessage(
          code === 'preview_converter_missing'
            ? tRef.current('attach.doc.converterMissing')
            : code === 'preview_conversion_failed'
              ? tRef.current('attach.doc.convertFailed')
              : tRef.current('attach.doc.previewFailed'),
        )
        setState('error')
      })
      .catch(() => {
        if (cancelled) return
        setMessage(tRef.current('attach.doc.previewFailed'))
        setState('error')
      })
    return () => {
      cancelled = true
    }
  }, [kind, url])

  if (state === 'checking') {
    return <p className="preview-note">{t('attach.doc.previewPreparing')}</p>
  }
  if (state === 'error') {
    return (
      <p className="preview-note" role="alert">
        {message}
      </p>
    )
  }

  if (kind === 'html') {
    if (!confirmed) {
      return (
        <div className="html-warning" role="alert">
          <strong>{t('attach.html.warningTitle')}</strong>
          <p>{t('attach.html.warningBody')}</p>
          <button type="button" onClick={() => setConfirmed(true)}>
            {t('attach.html.showAnyway')}
          </button>
        </div>
      )
    }
    return (
      <iframe
        src={url}
        className="doc-html-frame"
        title={filename}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-modals"
        referrerPolicy="no-referrer"
      />
    )
  }

  return <iframe src={url} className="doc-pdf-frame" title={filename} />
}

function TextPane({ text, truncated }: { text: string; truncated: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="doc-text">
      {truncated && (
        <p className="preview-note doc-text-truncated">{t('attach.doc.truncated')}</p>
      )}
      {/*
        `<pre>`: cikarilan metin GUVENILMEYEN icerik (kullanicinin yukledigi
        dosyadan geliyor). Markdown hattina sokmak, belgenin link ve
        bicimlendirme uretmesine izin vermek olurdu.
      */}
      <pre>{text}</pre>
    </div>
  )
}
